import React, {
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { oneOfType, node, string, bool, number } from 'prop-types';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import WarningIcon from 'assets/images/icons/warning.svg';

import styles from './Checkbox.module.scss';

const Checkbox = forwardRef(
  (
    { className, name, label, hasErrors, errorMessage, required, tabIndex },
    ref
  ) => {
    const inputId = useMemo(() => uuidv4(), []);
    const [error, setError] = useState(hasErrors);
    const inputRef = useRef(null);

    // eslint-disable-next-line no-unused-vars
    const validate = () => {
      let notValid = false;
      if (required) {
        if (!inputRef?.current.checked) {
          notValid = true;
          setError(true);
        } else {
          notValid = false;
          setError(false);
        }
      }
      return notValid;
    };

    const getValue = () => ({ value: String(inputRef?.current.checked), name });

    useImperativeHandle(ref, () => ({
      validate,
      getValue,
    }));

    return (
      <>
        <label
          htmlFor={inputId}
          className={clsx(styles.wrapper, className, {
            [styles.hasError]: error,
          })}
        >
          <input
            id={inputId}
            className={styles.input}
            tabIndex={tabIndex}
            type="checkbox"
            name={name}
            ref={inputRef}
          />

          <span className={styles.indicator} />
          <div className={styles.label}>{label}</div>
        </label>
        {error && (
          <div className={styles.error}>
            <WarningIcon />
            <span>{errorMessage}</span>
          </div>
        )}
      </>
    );
  }
);

Checkbox.propTypes = {
  name: string.isRequired,
  label: oneOfType([string, node]).isRequired,
  className: string,
  hasErrors: bool,
  errorMessage: string,
  required: bool,
  tabIndex: number,
};

Checkbox.defaultProps = {
  className: null,
  hasErrors: false,
  errorMessage: '',
  required: false,
  tabIndex: 0,
};

export default React.memo(Checkbox);
